import { useEffect, useMemo, useRef } from "react";
import ScanbotSDK from "scanbot-web-sdk/ui";
import {
  applyActionBarConfig,
  applyPaletteConfig,
  applySingleScanningUseCase,
} from "../scanbot-config";
import { VITE_SDK_LICENSE_KEY } from "@/env";

type SdkInstance = Awaited<ReturnType<typeof ScanbotSDK.initialize>>;
type BarcodeScannerUIResult = Awaited<
  ReturnType<typeof ScanbotSDK.UI.createBarcodeScanner>
>;

export function useScanbot() {
  const sdkRef = useRef<SdkInstance | null>(null);

  const config = useMemo(
    () => new ScanbotSDK.UI.Config.BarcodeScannerConfiguration(),
    [],
  );

  useEffect(() => {
    void (async () => {
      try {
        const sdkInstance = await ScanbotSDK.initialize({
          licenseKey: VITE_SDK_LICENSE_KEY || "",
          engine: "/wasm/",
        });
        sdkRef.current = sdkInstance;
      } catch (e) {
        console.error("Error initializing Scanbot SDK:", e);
      }
    })();
  }, []);

  const handleSingleScan = async (): Promise<string | undefined> => {
    if (!sdkRef.current) {
      console.error("Scanbot SDK is not initialized");
      return;
    }

    applySingleScanningUseCase(config);
    applyPaletteConfig(config);
    applyActionBarConfig(config);

    try {
      const result: BarcodeScannerUIResult | null =
        await ScanbotSDK.UI.createBarcodeScanner(config);

      return result?.items?.[0]?.barcode?.text;
    } catch (e) {
      console.error("Error during single scan:", e);
    }
  };

  return { handleSingleScan };
}
